// Pure helper functions for the ICICI CBDC MCC multi-select, where purpose
// codes act as bulk-select shortcuts for MCCs rather than a stored field.
//
// Behavior (per product decision): the final MCC selection is a flat,
// deduplicated list that can be built from any combination of purpose codes
// (select all MCCs within a purpose code, or hand-pick individual MCCs from
// within it), and remains freely editable afterward - there is no dependent
// link kept between a selected MCC and "which purpose code it came from".

export interface PurposeCodeOption {
  code: string
  description: string
  mccs: Record<string, string>
}

/**
 * Returns the list of MCC codes contained within a given purpose code option.
 */
export const getMccsForPurposeCode = (purposeCode: PurposeCodeOption): string[] => {
  return Object.keys(purposeCode.mccs || {})
}

/**
 * Adds every MCC within the given purpose code to the current selection,
 * de-duplicating against MCCs already selected (possibly from other purpose
 * codes).
 */
export const selectAllMccsInPurposeCode = (
  selectedMccs: string[],
  purposeCode: PurposeCodeOption,
): string[] => {
  const toAdd = getMccsForPurposeCode(purposeCode)
  return Array.from(new Set([...selectedMccs, ...toAdd]))
}

/**
 * Removes every MCC within the given purpose code from the current selection.
 * MCCs that also belong to another still-relevant purpose code but were
 * added via this one are still removed - the selection has no memory of
 * provenance, matching the "flat, freely editable list" model.
 */
export const deselectAllMccsInPurposeCode = (
  selectedMccs: string[],
  purposeCode: PurposeCodeOption,
): string[] => {
  const toRemove = new Set(getMccsForPurposeCode(purposeCode))
  return selectedMccs.filter(mcc => !toRemove.has(mcc))
}

/**
 * Returns true if every MCC within the purpose code is currently selected
 * (used to render the purpose code's "select all" checkbox as checked).
 */
export const areAllMccsInPurposeCodeSelected = (
  selectedMccs: string[],
  purposeCode: PurposeCodeOption,
): boolean => {
  const mccsInCode = getMccsForPurposeCode(purposeCode)
  if (mccsInCode.length === 0) return false
  const selectedSet = new Set(selectedMccs)
  return mccsInCode.every(mcc => selectedSet.has(mcc))
}

/**
 * Toggles a single MCC in/out of the selection.
 */
export const toggleMcc = (selectedMccs: string[], mcc: string): string[] => {
  if (selectedMccs.includes(mcc)) {
    return selectedMccs.filter(m => m !== mcc)
  }
  return [...selectedMccs, mcc]
}

/**
 * Unions the MCCs of every purpose code in `purposeCodes` whose code is in
 * `selectedPurposeCodes`, deduplicated. Used to build an aggregate "available
 * MCCs" pool when multiple purpose codes are expanded/selected at once.
 */
export const unionMccsAcrossPurposeCodes = (
  purposeCodes: PurposeCodeOption[],
  selectedPurposeCodes: string[],
): string[] => {
  const selectedSet = new Set(selectedPurposeCodes)
  const union = new Set<string>()
  purposeCodes
    .filter(pc => selectedSet.has(pc.code))
    .forEach(pc => {
      getMccsForPurposeCode(pc).forEach(mcc => union.add(mcc))
    })
  return Array.from(union)
}
